/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react';
import PropTypes from 'prop-types';

import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Renders social links from Contentful Footer (socialLinksCollection).
 * Returns null when socialLinks is missing or empty.
 */
const SocialIcons = ({socialLinks, variant, pinterestInnerColor = 'white', ...otherProps}) => {
  const styles = useMultiStyleConfig('SocialIcons', {variant});

  if (!socialLinks || socialLinks.length === 0) {
    return null;
  }

  return (
    <Flex
      className="sf-social-icons"
      {...styles.container}
      sx={{'--pinterest-icon-inner': pinterestInnerColor}}
      {...otherProps}
    >
      {socialLinks.filter(Boolean).map((link, index) => {
        const href = link.url || '#';
        const target = link.openInNewTab ? '_blank' : undefined;
        const rel = link.openInNewTab ? 'noopener noreferrer' : undefined;
        const ariaLabel = link.linkText || link.text || link.image?.title || `Social link ${index + 1}`;
        const imageUrl = link.image?.url;

        return (
          <Box
            key={link.sys?.id || index}
            aria-label={ariaLabel}
            as="a"
            href={href}
            rel={rel}
            target={target}
            {...styles.item}
          >
            {imageUrl ? (
              <Box
                alt={ariaLabel}
                as="img"
                height="full"
                maxHeight="full"
                maxWidth="full"
                src={imageUrl}
                width="full"
                {...styles.icon}
              />
            ) : (
              <Box as="span" {...styles.icon}>
                {link.linkText ?? link.text}
              </Box>
            )}
          </Box>
        );
      })}
    </Flex>
  );
};

SocialIcons.displayName = 'SocialIcons';

SocialIcons.propTypes = {
  /**
   * From Contentful Footer socialLinksCollection. When provided, renders these links with optional image icons.
   */
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
      openInNewTab: PropTypes.bool,
      sys: PropTypes.shape({id: PropTypes.string}),
      linkText: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
    })
  ),

  /**
   * This component has 3 variants, 'flex', 'flex-start' and 'flex-end'.
   * All will affect how the child icons are displayed. By default, it's
   * value is `flex`.
   */
  variant: PropTypes.string,

  /**
   * The inverse color of Pinterest icon's `currentColor`. For example, if the pinterest icon is white, then its inner 'p' is black.
   */
  pinterestInnerColor: PropTypes.string,
};

export default SocialIcons;
